import React, { useEffect, useRef, useState } from "react";
import { useAuth, useClerk, useSession } from "@clerk/clerk-react";
import { useNavigate, useLocation } from "react-router-dom";
import { exchangeClerkSession } from "../lib/clerkSession";
import {
  clearClerkOAuthPending,
  isClerkOAuthPending,
  waitForClerkToken,
} from "../lib/clerkBridge";

type Props = {
  setToken: (token: string) => void;
  hasAppToken: boolean;
};

/**
 * When Clerk has an active browser session but Drivenow has no app JWT,
 * exchange the Clerk token for our HS256 JWT.
 *
 * Important: AuthenticateWithRedirectCallback often navigates away from
 * /sso-callback to `/` before we sync. Password users never create a Clerk
 * session, so syncing on any route when isSignedIn is safe.
 */
export default function ClerkAppBridge({ setToken, hasAppToken }: Props) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { session } = useSession();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const location = useLocation();
  const inFlight = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (!isLoaded || hasAppToken) {
      return;
    }
    if (!isSignedIn) {
      return;
    }
    // Wait for an active session — pending task / incomplete Apple signup
    // reports signed-in-ish states without a usable token.
    if (session && session.status && session.status !== "active") {
      return;
    }
    if (inFlight.current) {
      return;
    }

    inFlight.current = true;
    setSyncing(true);
    setError(null);

    (async () => {
      try {
        const clerkToken = await waitForClerkToken(getToken);
        const result = await exchangeClerkSession(clerkToken);
        clearClerkOAuthPending();
        setToken(result.token);
        if (
          location.pathname.startsWith("/sso-callback") ||
          location.pathname === "/login" ||
          location.pathname === "/signup"
        ) {
          navigate("/", { replace: true });
        }
      } catch (err) {
        console.error("Clerk → Drivenow session sync failed:", err);
        clearClerkOAuthPending();
        const message =
          err instanceof Error ? err.message : "Social sign-in failed. Please try again.";
        setError(message);
        try {
          await signOut();
        } catch {
          // ignore
        }
        if (location.pathname.startsWith("/sso-callback")) {
          navigate("/login", { replace: true });
          alert(message);
        }
      } finally {
        inFlight.current = false;
        setSyncing(false);
      }
    })();
  }, [
    isLoaded,
    isSignedIn,
    hasAppToken,
    getToken,
    setToken,
    navigate,
    location.pathname,
    signOut,
    session,
  ]);

  // Visible “finishing sign-in” while we sync after OAuth lands on `/`.
  if (!hasAppToken && (syncing || isClerkOAuthPending()) && isSignedIn) {
    return (
      <div
        className="Auth-form-container"
        data-testid="clerk-bridge-syncing"
        style={{ zIndex: 1000 }}
      >
        <div className="Auth-form">
          <div className="Auth-form-content" style={{ textAlign: "center", paddingTop: 40 }}>
            <h3 className="Auth-form-title">Finishing sign-in…</h3>
            <p>Connecting your account to DriveNow.</p>
            {error ? (
              <p className="Auth-social-error" role="alert">
                {error}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return null;
}
